import { LEAD_SOURCE_TYPE_JOURNEY } from '@/constants/leadsourceTypes';
import { JourneyModel } from '@/typings/models/Journey';
import { createSelector } from 'reselect';

const selectJourneyState = (state: DefaultRootState) => state.journey;

export const selectJourney = createSelector(
  [selectJourneyState],
  (journey) => journey
);

export const selectJourneys = createSelector(selectJourney, (journey) => journey?.list || [])
export const selectSelectedJourney = createSelector(selectJourney, journey => journey?.selectedJourney || {})
export const selectConversionalJourneys = createSelector(selectJourney, (journey) => journey?.list?.filter(j => j.type === LEAD_SOURCE_TYPE_JOURNEY) || [])
export const selectJourneysInitialized = createSelector(selectJourney, (journey) => journey?.initialized || false)

/**
 * The journey the sidebar selector is actually showing.
 *
 * `journey.selectedJourney` holds one selection for every lead-source type, so
 * it can carry something that is not a journey at all; the sidebar accordion
 * falls back to the first real journey in that case. Anything that has to agree
 * with what the sidebar shows reads this rather than `selectSelectedJourney`.
 */
export const selectActiveJourney = createSelector(
  [selectSelectedJourney, selectConversionalJourneys],
  // `selectSelectedJourney` answers `{}` on an empty store, which is what makes
  // the annotation worth having: consumers read `.hashId` off the result.
  (selected, journeys): JourneyModel | null => {
    const current = selected as JourneyModel;
    return current?.type === LEAD_SOURCE_TYPE_JOURNEY ? current : journeys[0] ?? null;
  },
)
/**
 * Where a journey-scoped page starts when nothing is selected yet.
 *
 * The partner's **default lead source** first: its configuration is the one
 * every other journey inherits, so it is the one a Touchpoints page should open
 * on — landing on a journey that merely follows it would open the editor
 * read-only for no reason the user asked for.
 *
 * Failing that, Touchpoints surfaces let a partner pick *any* lead source — a
 * CSV or webhook source has a theme and sends emails too — but a real journey
 * is the one that uses all three channels, so it wins.
 */
export const selectDefaultLeadSource = createSelector(
  [selectJourneys, selectConversionalJourneys],
  (leadSources, journeys): JourneyModel | null =>
    leadSources.find((leadSource) => leadSource.is_default) ??
    journeys[0] ??
    leadSources[0] ??
    null,
)

export const selectJourneyConfigurations = createSelector(selectJourney, (journey) => journey?.config || false)

export const selectJourneyConfiguration = createSelector(
  [selectJourneyConfigurations, (_, journeyId) => journeyId],
  (configuration, journeyId) => configuration?.[journeyId] || {},
);
